import { utils } from 'frontend-js-shared'
import countries from '@/lib/countries.js'
import domainsBlacklist from '@/containers/Admin/OrganizationsView/DomainsSelector/domainsBlacklist.js'
import defaultEmojiTable from '@/lib/defaultEmojiTable.js'

const MAX_FILE_SIZE_IN_MB = 5
const MAX_FILE_SIZE_IN_BYTES = MAX_FILE_SIZE_IN_MB * 1024 * 1024
const FREE_TRIAL_DURATION_IN_DAYS = 7
const PREDEFINED_DATE_RANGES = utils.dates.PREDEFINED_DATE_RANGES

export const SCORING_MODES = Object.freeze({
  QUESTION: 'question',
  SECTION: 'section',
})

export const SCORING_ITEM_TYPES = Object.freeze({
  TEXT: 'text',
  SEPARATOR: 'separator',
})

export const ADMIN_ROLES = Object.freeze({
  ORGANIZATION_ADMIN: 'isOrganizationAdmin',
  SITE_ADMIN: 'isSiteAdmin',
  NONE: 'none',
})

export default Object.freeze({
  freeTrialDurationInMilliseconds:
    FREE_TRIAL_DURATION_IN_DAYS * 24 * 60 * 60 * 1000,

  supportedLanguages: [
    'en',
    'cs',
    'da',
    'de',
    'es',
    'fi',
    'fr',
    'it',
    'ja',
    'ko',
    'lt',
    'pt',
    'ro',
    'ru',
    'tr',
    'uk',
    'zh',
    'zu',
  ],
  //Countries list is from this repo: https://github.com/stefangabos/world_countries
  countries: countries,
  defaultEmojiTable: defaultEmojiTable,
  maxTagsPerGroup: 100,
  layouts: {
    blank: 'blank-layout',
    default: 'default-layout',
  },
  accountStatuses: Object.freeze({
    PENDING: 'pending',
    JOINED: 'joined',
    CREATED: 'created',
  }),
  rev: {
    teamMembersMaxLength: 5,
    benefitEstimationMaxLength: 1200,
    minSignificantPredictedFinancialImpact: 100,
  },
  revModelTypes: {
    improvementIdea: 'revs',
    problemSolving: 'problem-solving',
    quickAction: 'quick-actions',
  },
  revTypes: {
    improvementIdea: 'improvementIdea',
    problemSolving: 'problemSolving',
    quickAction: 'quickAction',
  },
  revTypeRouteNames: Object.freeze({
    quick_action: 'quickAction',
    improvement_idea: 'improvementIdea',
    problem_solving: 'problemSolving',
  }),
  validationRequestStatus: {
    inProgress: 'inProgress',
    canceled: 'canceled',
    completed: 'completed',
  },
  improvementIdea: {
    titleMaxLength: 140,
    solutionMaxLength: 1200,
    whatDidYouDoMaxLength: 1200,
    whatIsTheProblemMaxLength: 1200,
    whatIsTheIdeaMaxLength: 1200,
    expectedResultMaxLength: 1200,
    estimateBenefitsMaxLength: 1200,
    whatDidYouLearnMaxLength: 1200,
    phases: {
      relate: 'relate',
      experiment: 'experiment',
      verify: 'verify',
      expand: 'expand',
      review: 'review',
    },
  },
  problemSolving: {
    maxWhys: 10,
    phases: {
      definition: 'definition',
      measure: 'measure',
      analysis: 'analysis',
      solutions: 'solutions',
      review: 'review',
    },
    titleMaxLength: 90,
    maxLengthPreventiveAction: 1200,
    problemMaxLength: 1200,
    containmentPlanMaxLength: 1200,
    goalsMaxLength: 1200,
    businessRequirementsMaxLength: 1200,
    measureDescriptionMaxLength: 1200,
    solutionDescriptionMaxLength: 1200,
    maxRootCauses: 10,
    rootCauseMaxLength: 1200,
    correctiveActionLength: 1200,
    calculatingImpact: 1200,
    countermeasuresApproaches: [
      {
        value: 'containment',
        name:
          'problem_solving.problem_analysis.countermeasures_approaches.develop',
      },
      {
        value: 'goals',
        name:
          'problem_solving.problem_analysis.countermeasures_approaches.specific_goals',
      },
    ],
    rootCauseTypes: [
      {
        value: 'method',
        name: 'problem_solving.root_causes.types.method',
      },
      {
        value: 'machine',
        name: 'problem_solving.root_causes.types.machine',
      },
      {
        value: 'man',
        name: 'problem_solving.root_causes.types.people',
      },
      {
        value: 'material',
        name: 'problem_solving.root_causes.types.material',
      },
      {
        value: 'medium',
        name: 'problem_solving.root_causes.types.environment',
      },
      {
        value: 'other',
        name: 'problem_solving.root_causes.types.other',
      },
    ],
    defaultCountermeasuresApproach: 'containment',
  },
  quickAction: {
    titleMaxLength: 140,
    solutionMaxLength: 1200,
    problemMaxLength: 1200,
    calculatingImpact: 1200,
    phases: { before: 'before', after: 'after', review: 'review' },
  },
  organization: {
    nameMaxLength: 40,
    maxSitesAvailable: 5000,
    maxLicenses: 5000000,
    authenticationTypes: [
      {
        value: 'local',
        name: 'organization.local_authentication',
      },
      {
        value: 'local_and_google',
        name: 'organization.local_authentication_and_google',
      },
      {
        value: 'google',
        name: 'Google',
      },
      {
        value: 'azure_active_directory',
        name: 'Azure Active Directory',
      },
      {
        value: 'local_and_azure_active_directory',
        name: 'organization.local_authentication_and_active_directory',
      },
      {
        value: 'saml',
        name: 'SAML',
      },
    ],

    domainsBlacklist,

    organizationTypes: [
      {
        value: 'customer',
        name: 'Customer',
      },
      {
        value: 'internal',
        name: 'Internal',
      },
      {
        value: 'pilot',
        name: 'Pilot',
      },
    ],

    organizationPlans: [
      {
        value: 'essentials',
        name: 'Essentials (Free)',
      },
      {
        value: 'business',
        name: 'Business',
      },
      {
        value: 'enterprise',
        name: 'Enterprise',
      },
    ],

    industries: [
      {
        text: 'Manufacturing',
        value: 'Manufacturing',
      },
      {
        text: 'Health Care',
        value: 'Health Care',
      },
      {
        text: 'Technology (software, communications, media)',
        value: 'Technology',
      },
      {
        text: 'Oil & Gas',
        value: 'Oil & Gas',
      },
      {
        text: 'Financial Services & Insurance',
        value: 'Financial Services',
      },
      {
        text: 'Professional / Consulting Services',
        value: 'Consulting Services',
      },
      {
        text: 'Utilities & Public Sector',
        value: 'Utilities',
      },
      {
        text: 'Other',
        value: 'Other',
      },
    ],

    mediaProviders: [
      {
        value: 'AWS_S3',
        name: 'AWS S3 (default)',
      },
      {
        value: 'EXTERNAL_MINIO',
        name: 'External Minio (paid option)',
      },
      {
        value: 'EXTERNAL_AZURE_STORAGE_SERVICE',
        name: 'External Azure Storage Service (paid option)',
      },
    ],
  },

  onboarding: {
    maximumAllowedAccountsToCreate: 25,
  },

  urls: {
    android:
      'https://play.google.com/store/apps/details?id=com.reverscore.mobileapp',
    helpCenter: 'https://help.reverscore.net',
    ios: 'https://apps.apple.com/us/app/rever-score/id1114875070',
  },

  site: {
    nameMaxLength: 100,
    minRevsLoadPerCoach: 1,
  },

  supportedErrors: [401, 403, 404, 406, 500],

  generalErrors: ['GE001'],

  attachments: {
    maxFileSizeInBytes: MAX_FILE_SIZE_IN_BYTES,
    maxFileSizeInMB: MAX_FILE_SIZE_IN_MB,
    acceptedFormats: [
      'text/plain',
      'text/csv',
      'application/pdf',
      'application/zip',
      'application/x-zip-compressed',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.oasis.opendocument.text',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.oasis.opendocument.presentation',
      'image/gif',
      'image/png',
      'image/jpeg',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ],
  },

  fileAttachments: {
    defaultLimit: 5,
    maxFileSizeInBytes: MAX_FILE_SIZE_IN_BYTES,
    maxFileSizeInMB: MAX_FILE_SIZE_IN_MB,
    acceptedFormats: [
      'text/plain',
      'text/csv',
      'application/pdf',
      'application/zip',
      'application/x-zip-compressed',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.oasis.opendocument.text',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.oasis.opendocument.presentation',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ],
  },

  images: {
    defaultLimit: 5,
    maxFileSizeInBytes: MAX_FILE_SIZE_IN_BYTES,
    maxFileSizeInMB: MAX_FILE_SIZE_IN_MB,
    acceptedMimeTypes: ['image/gif', 'image/png', 'image/jpeg', 'image/jpg'],
    acceptedExtensions: ['jpg', 'jpeg', 'png', 'gif'],
  },

  files: {
    maxFileSizeInBytes: MAX_FILE_SIZE_IN_BYTES,
    maxFileSizeInMB: MAX_FILE_SIZE_IN_MB,
    acceptedCSVExtension: ['csv'],
    acceptedCSVType: [
      'text/csv',
      'application/csv',
      'application/x-csv',
      'application/vnd.ms-excel',
      'text/comma-separated-values',
      'text/x-comma-separated-values',
      'text/tab-separated-values',
    ],
  },

  profile: {
    skillsMaxLength: 40,
    skillsMaxCount: 30,
    positionMaxLength: 100,
  },

  findings: {
    situationMaxLength: 140,
    solutionMaxLength: 1200,
    status: {
      open: 'open',
      inProgress: 'inProgress',
      inReview: 'inReview',
      completed: 'completed',
      canceled: 'canceled',
      onHold: 'onHold',
    },
  },

  routines: {
    advancedSelectorOptions: [
      {
        name: 'routines.advanced_selector.all_routines_option',
        value: 'allRoutines',
      },
      {
        name: 'routines.advanced_selector.executable_by_me_option',
        value: 'executableByMe',
      },
      {
        name: 'routines.advanced_selector.created_by_me_option',
        value: 'createdByMe',
      },
      {
        name: 'routines.advanced_selector.im_collaborator_option',
        value: 'collaborator',
      },
    ],
    assignees: {
      maxLength: 5,
    },
    definition: {
      titleMaxLength: 140,
    },
    frequencies: [
      {
        value: 'shift',
        name: 'routines.frequency.shift',
      },
      {
        value: 'daily',
        name: 'routines.frequency.daily',
      },
      {
        value: 'weekly',
        name: 'routines.frequency.weekly',
      },
      {
        value: 'biweekly',
        name: 'routines.frequency.biweekly',
      },
      {
        value: 'monthly',
        name: 'routines.frequency.monthly',
      },
      {
        value: 'quarterly',
        name: 'routines.frequency.quarterly',
      },
      {
        value: 'halfYear',
        name: 'routines.frequency.halfYear',
      },
      {
        value: 'onDemand',
        name: 'routines.frequency.onDemand',
      },
    ],
    types: [
      {
        value: '5s_audit',
        name: 'routines.type.5s_audit',
      },
      {
        value: 'maintenance',
        name: 'routines.type.maintenance',
      },
      {
        value: 'gemba',
        name: 'routines.type.gemba',
      },
      {
        value: 'quality_control',
        name: 'routines.type.quality_control',
      },
      {
        value: 'communication',
        name: 'routines.type.communication',
      },
      {
        value: 'financial_tracking',
        name: 'routines.type.financial_tracking',
      },
    ],
    executions: {
      notesMaxlength: 5000,
    },
  },

  comments: {
    maxLength: 1000,
  },

  thirdPartyAuthProviders: ['google', 'azure_active_directory', 'saml'],

  priorities: { low: 'low', medium: 'medium', high: 'high' },

  revStatus: {
    open: 'open',
    inProgress: 'inProgress',
    inReview: 'inReview',
    completed: 'completed',
    canceled: 'canceled',
    onHold: 'onHold',
    learned: 'learned',
    validated: 'validated',
    financiallyValidated: 'financiallyValidated',
    impactValidated: 'impactValidated',
  },

  exploreEntityStatus: {
    open: 'open',
    inProgress: 'inProgress',
    completed: 'completed',
    onHold: 'onHold',
    inReview: 'inReview',
    learned: 'learned',
    canceled: 'canceled',
    active: 'active',
    closed: 'closed',
    soon: 'soon',
  },

  exploreEntityTypes: {
    improvementIdea: 'improvementIdea',
    problemSolving: 'problemSolving',
    quickAction: 'quickAction',
    routine: 'routine',
    finding: 'finding',
    challenge: 'challenge',
    task: 'task',
  },

  exploreRoleTypes: {
    coach: 'coach',
    owner: 'owner',
    collaborators: 'collaborators',
  },

  checklist: {
    titleMaxLength: 140,
    steps: {
      configuration: 1,
      setup: 2,
      review: 3,
    },
    availableEntities: {
      quickAction: 'quickAction',
      improvementIdea: 'improvementIdea',
      problemSolving: 'problemSolving',
      routine: 'routine',
    },
  },

  investments: [
    {
      id: 'financial',
      name: 'financial',
      units: [{ magnitude: null, name: 'currency' }],
    },

    {
      id: 'time',
      name: 'time',
      units: [
        { name: 'minutes', magnitude: 'time' },
        { name: 'hours', magnitude: 'time' },
        { name: 'days', magnitude: 'time' },
        { name: 'weeks', magnitude: 'time' },
      ],
    },
  ],

  metricsType: {
    reduction: 'reduction',
    increment: 'increment',
  },

  metricUnitTypes: {
    energy: 'energy',
    currency: 'currency',
    time: 'time',
    weight: 'weight',
    volume: 'volume',
    percentage: 'percentage',
  },

  KEY_CODES: {
    BACKSPACE: 8,
    ZERO: 48,
    NINE: 57,
    NUM_PAD_ZERO: 96,
    NUM_PAD_NINE: 105,
    TAB: 9,
    DOT: 190,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
  },

  validationsFilters: {
    validations: {
      assignedToMe: 'assignedToMe',
      pending: 'pending',
      validated: 'validated',
    },
    contentTab: {
      implementation: 0,
      impact: 1,
    },
  },

  DATE_FILTER_OPTIONS: {
    ...PREDEFINED_DATE_RANGES,
    all: 'all',
    custom: 'custom',
    currentMonth: 'currentMonth',
    currentWeek: 'currentWeek',
  },

  SEARCH_SCOPES: {
    site: 'site',
    global: 'global',
  },

  tasksStatus: {
    open: 'open',
    completed: 'completed',
    inProgress: 'inProgress',
  },

  ICONS_BY_ENTITY: {
    improvementIdea: 'mdi-lightbulb-outline',
    problemSolving: 'mdi-puzzle-outline',
    quickAction: 'mdi-lightning-bolt-outline',
    routine: 'mdi-clipboard-text-outline',
    finding: 'mdi-toy-brick-search-outline',
    challenge: 'mdi-target',
    task: 'mdi-check',
  },

  ICON_COLORS_BY_ENTITY: {
    improvementIdea: 'blue',
    problemSolving: 'red',
    quickAction: 'orange',
    routine: 'teal',
    finding: 'blue-grey darken-1',
    challenge: 'purple',
    task: 'green',
  },

  BACKGROUND_COLORS_BY_ENTITY: {
    rev: 'blue lighten-2',
    improvementIdea: 'blue lighten-2',
    problemSolving: 'red lighten-2',
    quickAction: 'orange lighten-2',
    routine: 'teal lighten-2',
    finding: 'blue-grey lighten-2',
    challenge: 'purple lighten-2',
    task: 'green lighten-2',
  },

  ACTIVE_BACKGROUND_COLORS_BY_ENTITY: {
    rev: 'blue',
    improvementIdea: 'blue',
    problemSolving: 'red',
    quickAction: 'orange',
    routine: 'teal',
    finding: 'blue-grey',
    challenge: 'purple',
    task: 'green',
  },

  COLOR_BY_STATUS: {
    open: 'teal lighten-2',
    active: 'teal lighten-2',
    inProgress: 'blue lighten-2',
    completed: 'green lighten-2',
    closed: 'green lighten-2',
    onHold: 'yellow lighten-2',
    inReview: 'yellow lighten-2',
    soon: 'purple lighten-2',
    learned: 'purple lighten-2',
    canceled: 'blue-grey lighten-2',
  },

  TEXT_COLOR_BY_STATUS: {
    open: 'teal--text text--darken-1',
    active: 'teal--text text--darken-1',
    inProgress: 'blue--text text--darken-1',
    completed: 'green--text text--darken-1',
    closed: 'green--text text--darken-1',
    onHold: 'yellow--text text--darken-1',
    inReview: 'yellow--text text--darken-1',
    soon: 'purple--text text--darken-1',
    learned: 'purple--text text--darken-1',
    canceled: 'blue-grey--text text--darken-2',
  },

  PRIORITY_PROPS: {
    high: {
      iconColor: 'red',
      iconName: 'mdi-chevron-double-up',
      backgroundColor: 'red lighten-2',
    },
    medium: {
      iconColor: 'yellow',
      iconName: 'mdi-equal',
      backgroundColor: 'yellow lighten-2',
    },
    low: {
      iconColor: 'blue',
      iconName: 'mdi-chevron-double-down',
      backgroundColor: 'blue lighten-2',
    },
  },

  EMPTY_COLUMN: {
    name: '',
    filter: {},
    sort: { by: 'createdAt', order: 'desc' },
    position: 0,
  },

  TASKS_VIEW_OPTIONS: {
    list: 'list',
    kanban: 'kanban',
  },

  TASK: {
    nameMaxLength: 2500,
    descriptionMaxLength: 2500,
  },

  PROMPTS: {
    quickAction: 'show me a new quick action that I can implement',
    improvementIdea: 'show me a new improvement idea that I can implement',
    problemSolving: 'show me a new problem solving that I can implement',
    routine: 'show me a new routine that I can implement',
    finding: 'show me a new finding that I can implement',
    challenge: 'show me a new challenge that I can implement',
    task: 'show me a new task that I can implement',
  },
})
